import { useMemo, useState, useCallback } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { AlertTriangle, Download, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { formatISODate, type DateRange } from '@/lib/salesAnalytics';
import {
  fetchSellThrough, fetchSellThroughCoverage, fetchLatestSnapshotDate,
  type SellThroughRow,
} from '@/lib/api/sellThrough';
import { fetchBuildStock } from '@/lib/api/buildStock';

/**
 * Sell-through against buildability: what each model sold in the window, what
 * was posted for it, what is still on the shelf, and how many more the parts
 * bin allows.
 *
 * Two things this panel must never let the reader forget, because both make a
 * number look smaller or larger than the business reality:
 *
 *  - Sold is matched BY SKU, so it agrees with the sales tab. The only volume
 *    it can miss is a sold item_no with no build record, which the coverage
 *    line names outright - a silent shortfall would be worse than a visible
 *    one.
 *  - Can build is availability AS OF NOW, against a window that is usually in
 *    the past. Comparing this quarter's sales to today's shelf is the useful
 *    question ("can we keep selling this?"), but it is not a like-for-like
 *    period comparison and is labelled so it is not read as one.
 */
export function SellThroughPanel({ range }: { range: DateRange }) {
  const [search, setSearch] = useState('');

  const { data: rows, isLoading, isPlaceholderData, error } = useQuery({
    queryKey: ['model-sell-through', range.from, range.to],
    queryFn: () => fetchSellThrough(range),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  const { data: coverage } = useQuery({
    queryKey: ['model-sell-through-coverage', range.from, range.to],
    queryFn: () => fetchSellThroughCoverage(range),
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });

  // On-hand comes from here, not from model_sell_through.
  //
  // model_sell_through derives on_hand from assembly_order_serials: machines
  // whose serial is recorded and that have not been net-sold. That answers
  // "which machines can we individually account for", which is a different
  // question from "how many do we have" - serial capture only begins
  // 2026-03-11, and the 447 orders backfilled that March carry five serials
  // between them. So it read 45 against a real 53.
  //
  // Whether someone typed a serial is a fact about our records, not about the
  // shelf. build_stock() is anchored to a Business Central count, so it counts
  // every machine and lets the unserialised ones simply be unserialised.
  const { data: stockByBuild } = useQuery({
    queryKey: ['build-stock'],
    queryFn: fetchBuildStock,
    staleTime: 60_000,
  });

  // Absent until the first nightly capture runs. Distinguishes "no history yet"
  // from "history is stale", which look identical if you only show a date.
  const { data: snapshotDate } = useQuery({
    queryKey: ['latest-stock-snapshot'],
    queryFn: fetchLatestSnapshotDate,
    staleTime: 5 * 60 * 1000,
  });

  const all = useMemo(() => rows ?? [], [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter(r =>
      r.buildSku.toLowerCase().includes(q) ||
      (r.description ?? '').toLowerCase().includes(q) ||
      (r.limitingSku ?? '').toLowerCase().includes(q));
  }, [all, search]);

  /**
   * On hand for one row, preferring the BC-anchored figure.
   *
   * Falls back to the serial-derived number only for a build that has no
   * opening balance at all, which after the 2026-09-24 sync is none of them -
   * it exists so a build added later shows something rather than a blank.
   */
  const stockOnHand = useCallback(
    (r: { buildId: string; onHand: number }) =>
      stockByBuild?.get(r.buildId)?.on_hand ?? r.onHand,
    [stockByBuild],
  );

  const totals = useMemo(() => {
    let sold = 0, built = 0, blocked = 0;
    for (const r of all) {
      sold += r.sold;
      built += r.built;
      // Sold in the window but nothing left to build is the row worth acting on.
      if (r.sold > 0 && r.canBuild === 0) blocked++;
    }
    return { sold, built, blocked };
  }, [all]);

  /**
   * Stock on hand across every model, right now.
   *
   * Deliberately NOT summed over the rows above. Sold and built are flows and
   * belong to the selected range; stock is a level and belongs to a moment.
   * Summing it over a window-filtered row set made the figure shrink as the
   * range narrowed - 53 on All time, less on anything shorter - which read as
   * "we had less stock in June" when it only meant fewer models had activity
   * in June.
   *
   * So it spans all builds, not the visible ones, and does not move when the
   * range does. The consequence is that it will not equal the column beneath
   * it on a narrow range; the label and title say so rather than leaving the
   * reader to notice and distrust both.
   */
  const onHandNow = useMemo(() => {
    if (!stockByBuild) return null;
    let total = 0;
    for (const row of stockByBuild.values()) total += row.on_hand ?? 0;
    return total;
  }, [stockByBuild]);

  const openingAt = stockByBuild
    ? [...stockByBuild.values()].find(r => r.opening_at)?.opening_at ?? null
    : null;

  const exportCsv = () => {
    const header = [
      'Build SKU', 'Model', 'Sold', 'Built', 'On hand', 'Can build',
      'Limited by', 'Limiting available', 'Config assumed',
    ];
    const escape = (v: unknown) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const lines = [header.join(',')];
    for (const r of filtered) {
      lines.push([
        r.buildSku, r.description ?? '', r.sold, r.built, stockOnHand(r), r.canBuild,
        r.limitingSku ?? '', r.limitingAvailable ?? '', r.assumedConfig ? 'yes' : '',
      ].map(escape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sell-through-${range.from}-to-${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          Could not load sell-through: {error instanceof Error ? error.message : 'unknown error'}
        </CardContent>
      </Card>
    );
  }

  if (isLoading && !rows) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  const unattributed = coverage ? coverage.totalUnits - coverage.attributedUnits : 0;

  return (
    <div className={cn('space-y-4 transition-opacity', isPlaceholderData && 'opacity-60')}>
      {/* Coverage. Only shown when something is actually missing: a line
          announcing that all of the volume matched is noise on every load. */}
      {coverage && unattributed !== 0 && (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          <span className="text-foreground font-medium">
            {coverage.unmatchedUnits.toLocaleString()} of {coverage.totalUnits.toLocaleString()} units sold
          </span>
          {' '}have an item number with no build record, so they are in no row below
          {coverage.unmatchedSkus.length > 0 && (
            <> — <span className="font-mono">{coverage.unmatchedSkus.slice(0, 6).join(', ')}</span>
              {coverage.unmatchedSkus.length > 6 && ` +${coverage.unmatchedSkus.length - 6} more`}</>
          )}
        </div>
      )}

      {/* Summary strip */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <Figure label="sold" value={totals.sold} />
        <Figure label="built" value={totals.built} />
        {onHandNow != null && (
          <Figure
            label="on hand now — all models"
            value={onHandNow}
            title={
              'Stock across every model as of now, not just the models with activity in this range, ' +
              'so it does not change with the dates and will not match the column below on a short range.' +
              (openingAt ? ` Counted from the Business Central figure of ${openingAt}, plus units built and minus units sold since.` : '')
            }
          />
        )}
        {totals.blocked > 0 && (
          <span className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--delta-bad)' }}>
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            {totals.blocked} model{totals.blocked === 1 ? '' : 's'} sold with none left to build
          </span>
        )}
      </div>

      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0 pb-2 gap-3">
          <div>
            <CardTitle className="text-base">Sell-through vs buildability</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Sold and built over {formatISODate(range.from)} – {formatISODate(range.to)} ·
              {' '}on hand and can build are <span className="font-medium">as of now</span>
              {snapshotDate
                ? ` · daily stock history since ${formatISODate(snapshotDate)}`
                : ' · daily stock history starts with the first nightly capture'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                className="h-9 pl-8 w-[180px]"
                placeholder="SKU or model…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <Button variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {all.length === 0
                ? 'No build activity, stock or buildable models in this range'
                : 'No model matches that search'}
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground text-left">
                    <th className="py-2 pr-3 font-medium">Build SKU</th>
                    <th className="py-2 pr-3 font-medium">Model</th>
                    <th className="py-2 pr-3 font-medium text-right">Sold</th>
                    <th className="py-2 pr-3 font-medium text-right">Built</th>
                    <th className="py-2 pr-3 font-medium text-right">On hand</th>
                    <th className="py-2 pr-3 font-medium text-right">Can build</th>
                    <th className="py-2 font-medium">Limited by</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(r => <Row key={r.buildId} row={r} onHand={stockOnHand(r)} />)}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Figure({ label, value, title }: { label: string; value: number; title?: string }) {
  return (
    <span className="flex items-baseline gap-1.5" title={title}>
      <span className="text-lg font-semibold tabular-nums">{value.toLocaleString()}</span>
      <span className="text-xs text-muted-foreground">{label}</span>
    </span>
  );
}

function Row({ row, onHand }: { row: SellThroughRow; onHand: number }) {
  // Zero is only alarming for a model that is actually selling; a discontinued
  // build with no parts left is just discontinued.
  const blocked = row.sold > 0 && row.canBuild === 0;

  return (
    <tr className="border-b last:border-0 hover:bg-muted/40">
      <td className="py-2 pr-3 whitespace-nowrap font-mono text-xs">{row.buildSku}</td>
      <td className="py-2 pr-3">
        {row.description?.split(' - ')[0] ?? '—'}
        {row.assumedConfig && (
          <Badge
            variant="outline"
            className="ml-2 text-xs font-normal"
            title="No active config is ticked on this build, so the first component per category was assumed. Can build is an estimate."
          >
            config assumed
          </Badge>
        )}
      </td>
      <td className="py-2 pr-3 text-right tabular-nums">{row.sold.toLocaleString()}</td>
      <td className="py-2 pr-3 text-right tabular-nums text-muted-foreground">{row.built.toLocaleString()}</td>
      <td className="py-2 pr-3 text-right tabular-nums">{onHand.toLocaleString()}</td>
      <td
        className="py-2 pr-3 text-right tabular-nums font-medium"
        style={blocked ? { color: 'var(--delta-bad)' } : undefined}
      >
        {row.canBuild.toLocaleString()}
      </td>
      <td className="py-2 text-xs text-muted-foreground">
        {row.limitingSku ? (
          <span className="whitespace-nowrap">
            <span className="font-mono">{row.limitingSku}</span>
            {row.limitingAvailable != null && <> · {row.limitingAvailable} available</>}
          </span>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}
